"use strict";
/*
Copyright 2021 Splunk Inc.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.SplunkExporter = exports.NATIVE_BEACON_SENDER = exports.NATIVE_XHR_SENDER = exports.NOOP_ATTRIBUTES_TRANSFORMER = void 0;
const transform_js_1 = require("@opentelemetry/exporter-zipkin/build/src/transform.js");
const core_1 = require("@opentelemetry/core");
const utils_1 = require("./utils");
const MAX_VALUE_LIMIT = 4096;
const SPAN_RATE_LIMIT_PERIOD = 30000; // millis, sweep to clear out span counts
const MAX_SPANS_PER_PERIOD_PER_COMPONENT = 100;
const SERVICE_NAME = 'browser';
const NOOP_ATTRIBUTES_TRANSFORMER = (attributes) => attributes;
exports.NOOP_ATTRIBUTES_TRANSFORMER = NOOP_ATTRIBUTES_TRANSFORMER;
const NATIVE_XHR_SENDER = (url, data) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Accept', '*/*');
    xhr.setRequestHeader('Content-Type', 'text/plain;charset=UTF-8');
    xhr.send(data);
};
exports.NATIVE_XHR_SENDER = NATIVE_XHR_SENDER;
exports.NATIVE_BEACON_SENDER = navigator.sendBeacon ? (url, data) => navigator.sendBeacon(url, data) : null;
/**
 * SplunkExporter is based on Zipkin V2. It includes Splunk-specific modifications.
 */
class SplunkExporter {
    constructor({ beaconUrl, onAttributesSerializing = exports.NOOP_ATTRIBUTES_TRANSFORMER, xhrSender = exports.NATIVE_XHR_SENDER, beaconSender = exports.NATIVE_BEACON_SENDER, }) {
        this._spanCounts = new Map();
        this.beaconUrl = beaconUrl;
        this._onAttributesSerializing = onAttributesSerializing;
        this._xhrSender = xhrSender;
        this._beaconSender = beaconSender;
        this._limiterHandle = window.setInterval(() => {
            this._spanCounts.clear();
        }, SPAN_RATE_LIMIT_PERIOD);
    }
    export(spans, resultCallback) {
        spans = spans.filter(span => this._filter(span));
        const zspans = spans.map(span => this._mapToZipkinSpan(span));
        const zJson = JSON.stringify(zspans);
        if (this._beaconSender) {
            this._beaconSender(this.beaconUrl, zJson);
        }
        else {
            this._xhrSender(this.beaconUrl, zJson);
        }
        resultCallback({ code: core_1.ExportResultCode.SUCCESS });
    }
    shutdown() {
        clearInterval(this._limiterHandle);
        return Promise.resolve();
    }
    _filter(span) {
        var _a, _b;
        const component = ((_b = (_a = span.attributes) === null || _a === void 0 ? void 0 : _a.component) !== null && _b !== void 0 ? _b : 'unknown').toString();
        if (!this._spanCounts.has(component)) {
            this._spanCounts.set(component, -1);
        }
        const counter = this._spanCounts.get(component) + 1;
        this._spanCounts.set(component, counter);
        return counter < MAX_SPANS_PER_PERIOD_PER_COMPONENT;
    }
    _mapToZipkinSpan(span) {
        const preparedSpan = this._preTranslateSpan(span);
        const zspan = (0, transform_js_1.toZipkinSpan)(preparedSpan, SERVICE_NAME, transform_js_1.defaultStatusCodeTagName, transform_js_1.defaultStatusErrorTagName);
        return this._postTranslateSpan(zspan);
    }
    _preTranslateSpan(span) {
        return {
            // todo: once typescript is implemented, conform to ReadableSpan
            // note: some properties in Span are not enumerable, and as a result cannot be spread or Object.assign'ed
            name: span.name,
            kind: span.kind,
            spanContext: span.spanContext.bind(span),
            parentSpanId: span.parentSpanId,
            startTime: span.startTime,
            endTime: span.endTime,
            status: span.status,
            links: span.links,
            events: span.events,
            duration: span.duration,
            ended: span.ended,
            instrumentationLibrary: span.instrumentationLibrary,
            resource: span.resource,
            attributes: this._onAttributesSerializing(span.attributes, span),
        };
    }
    _postTranslateSpan(span) {
        delete span.localEndpoint;
        span.name = (0, utils_1.limitLen)(span.name, MAX_VALUE_LIMIT);
        for (const [key, value] of Object.entries(span.tags)) {
            span.tags[key] = (0, utils_1.limitLen)(value.toString(), MAX_VALUE_LIMIT);
        }
        return span;
    }
}
exports.SplunkExporter = SplunkExporter;
//# sourceMappingURL=SplunkExporter.js.map